/**
 * Shared hero shell – government-facing professional aesthetic
 * Clean, restrained, no flashy animations
 */

import React from 'react';
import { LandingNavbar } from './LandingNavbar';
import { LandingBeamButton } from './LandingBeamButton';
import styles from './LandingShared.module.css';
import type { LandingBranding } from './landingBranding';

export interface LandingHeroShellProps {
  branding: LandingBranding;
  loginHref: string;
  /** Main heading (e.g. "AI-Powered Proposal Intelligence") */
  heading: string;
  /** Keyword to highlight with gradient (e.g. "Intelligence" or "Opportunities") */
  gradientKeyword?: string;
  /** Subheading/description */
  description: string;
  /** Source / partner badges — horizontal infinite marquee below CTA (full width) */
  marqueeItems?: Array<{ label: string; icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; key?: string }>;
  /** Custom badge row (alternative to marqueeItems) */
  marqueeContent?: React.ReactNode;
  /** Whether to show the abstract dashboard on the right */
  showAbstractDashboard?: boolean;
  /** 'light' for SPARC/GovEarner (light-only apps), 'dark' for default */
  heroVariant?: 'light' | 'dark';
  /**
   * When true (typically with showAbstractDashboard=false), the hero does not fill the viewport
   * with vertically centered content — tighter top/bottom spacing around headline + marquee.
   */
  compactVerticalSpacing?: boolean;
}

export function LandingHeroShell({
  branding,
  loginHref,
  heading,
  gradientKeyword,
  description,
  marqueeItems,
  marqueeContent,
  showAbstractDashboard = true,
  heroVariant = 'dark',
  compactVerticalSpacing = false,
}: LandingHeroShellProps) {
  const headingParts =
    gradientKeyword && heading.includes(gradientKeyword)
      ? heading.split(gradientKeyword)
      : [heading, ''];
  const useGradient = gradientKeyword && heading.includes(gradientKeyword);
  const isLight = heroVariant === 'light';
  const heroClass = isLight ? styles.heroLight : styles.heroDark;

  const compactHero = compactVerticalSpacing && !showAbstractDashboard;

  return (
    <section
      className={`relative flex flex-col ${compactHero ? 'min-h-0' : 'min-h-[100dvh]'} ${heroClass}`}
      style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}
    >
      <div className={styles.heroGrid} aria-hidden />

      <LandingNavbar branding={branding} loginHref={loginHref} variant={heroVariant} />

      <div className={`relative z-10 flex flex-col ${compactHero ? '' : 'min-h-0 flex-1'}`}>
        {/* Main block: default = vertically centered; compact = tighter top/bottom (e.g. RFP landing) */}
        <div
          className={
            showAbstractDashboard
              ? 'flex min-h-0 flex-1 flex-col justify-center py-6 pb-8 md:py-8 md:pb-10'
              : compactHero
                ? 'flex flex-col justify-start pt-4 pb-6 md:pt-5 md:pb-8'
                : 'flex min-h-0 flex-1 flex-col justify-center py-6 pb-8 md:py-8 md:pb-10'
          }
        >
          <div className="mx-auto w-full max-w-5xl px-6">
            <div
              className={
                showAbstractDashboard
                  ? 'flex flex-col items-center gap-8 text-center md:flex-row md:items-center md:justify-between md:gap-12 md:text-left'
                  : 'mx-auto flex max-w-3xl flex-col items-center text-center'
              }
            >
              <div className={showAbstractDashboard ? 'min-w-0 flex-1' : ''}>
                <h1 className={`text-3xl font-bold leading-tight tracking-tight md:text-4xl lg:text-5xl ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  {useGradient && gradientKeyword ? (
                    <>
                      {headingParts[0]}
                      <span
                        className={styles.textGradient}
                        style={{
                          backgroundImage: `linear-gradient(90deg, ${branding.primaryColor}, ${branding.secondaryColor})`,
                        }}
                      >
                        {gradientKeyword}
                      </span>
                      {headingParts[1]}
                    </>
                  ) : (
                    heading
                  )}
                </h1>
                <p
                  className={`mt-4 text-lg md:text-xl ${showAbstractDashboard ? 'max-w-xl' : 'mx-auto max-w-2xl'} ${isLight ? 'text-slate-600' : 'text-slate-300'}`}
                >
                  {description}
                </p>
                <div
                  className={`${compactHero ? 'mt-6' : 'mt-8'} flex ${showAbstractDashboard ? 'justify-center md:justify-start' : 'justify-center'}`}
                >
                  <LandingBeamButton href={loginHref} primaryColor={branding.primaryColor}>
                    Log in
                  </LandingBeamButton>
                </div>
              </div>

              {showAbstractDashboard && (
                <div className="relative hidden h-64 w-80 flex-shrink-0 lg:block">
                  <div className={`absolute inset-0 rounded-xl ${styles.glowBorder}`} style={{ backgroundColor: isLight ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.2)' }}>
                    <div
                      className="absolute inset-0 rounded-xl opacity-30"
                      style={{
                        backgroundImage: `linear-gradient(${branding.primaryColor}22 1px, transparent 1px), linear-gradient(90deg, ${branding.primaryColor}22 1px, transparent 1px)`,
                        backgroundSize: '24px 24px',
                      }}
                    />
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className={styles.abstractCard} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Marquee matches headline column width (center band — not full viewport) */}
          {marqueeItems?.length ? (
            <div className={`mx-auto w-full max-w-5xl px-6 ${compactHero ? 'mt-6 md:mt-7' : 'mt-10'}`}>
              <div
                className={`mx-auto w-full min-w-0 ${showAbstractDashboard ? 'max-w-5xl' : 'max-w-3xl'}`}
              >
                <div className={`${styles.marqueeWrapper} w-full`}>
                  <div className="flex w-max">
                    <div className={styles.marqueeTrack}>
                      {[0, 1].map((copy) =>
                        marqueeItems!.map((item) => {
                          const Icon = item.icon;
                          const k = item.key ?? item.label;
                          return (
                            <span
                              key={`${copy}-${k}`}
                              className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${styles.marqueeBadge} ${isLight ? 'border-slate-200 bg-white/80 text-slate-700 shadow-sm' : 'border-white/10 bg-white/5 text-slate-300'}`}
                            >
                              {Icon && <Icon className="h-4 w-4 shrink-0" style={{ color: branding.primaryColor }} />}
                              {item.label}
                            </span>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : marqueeContent ? (
            <div className={`${compactHero ? 'mt-6' : 'mt-10'} flex w-full flex-wrap justify-center gap-4 px-6`}>{marqueeContent}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
